import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableCombobox } from '@/components/ui/searchable-combobox';
import { Progress } from '@/components/ui/progress';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChevronLeft, ChevronRight, CheckCircle2, User, ClipboardList, Trophy, Loader2, HelpCircle, AlertCircle, CalendarIcon, Building2, History, TrendingUp, TrendingDown, Minus, Brain, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import LevelLadder from '@/components/LevelLadder';
import { diagnosticQuestions, DiagnosticLevel, levelInfo, DiagnosticAnswer } from '@/data/diagnosticQuestions';
import { toast } from 'sonner';
import DiagnosticOnboarding from '@/components/DiagnosticOnboarding';
import { useAuth } from '@/contexts/AuthContext';
import {
  useDiagnosticResult,
  useSaveDiagnostic,
  useDiagnosticHistory,
  useEvaluateDiagnostic,
  useLatestParticipantDraft,
  useDiscardParticipantDraft,
  useParticipantDraftAutosave,
} from '@/hooks/useDiagnostic';
import { useDraftAutosave, loadDraft, clearDraft } from '@/hooks/useDraftAutosave';
import { formatDistanceToNow } from 'date-fns';

import { supabase } from '@/integrations/supabase/client';

interface DiagnosticDraft {
  step: 'personal' | 'questions';
  personalInfo: {
    birthDate?: string;
    cargo: string;
    nombreEmpresa: string;
    sector: string;
    area: string;
  };
  answers: DiagnosticAnswer[];
  currentQuestionIndex: number;
}

interface PersonalInfo {
  birthDate: Date | undefined;
  cargo: string;
  nombreEmpresa: string;
  sector: string;
  area: string;
}

type Step = 'onboarding' | 'personal' | 'questions' | 'evaluating' | 'results';

const hasSeenOnboarding = () => localStorage.getItem('diagnostic_onboarding_seen') === 'true';
const markOnboardingSeen = () => localStorage.setItem('diagnostic_onboarding_seen', 'true');

const createEmptyAnswers = (): DiagnosticAnswer[] =>
  new Array(diagnosticQuestions.length).fill(null).map(() => ({ text: '', cannotAnswer: false }));

export default function DiagnosticPage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const orgName = profile?.company;
  const { data: existingDiag, isLoading } = useDiagnosticResult();
  const { data: diagnosticHistory = [] } = useDiagnosticHistory();
  const saveMutation = useSaveDiagnostic();
  const evaluateMutation = useEvaluateDiagnostic();

  const { data: orgData } = useQuery({
    queryKey: ['org-basic', profile?.organization_id],
    queryFn: async () => {
      if (!profile?.organization_id) return null;
      const { data } = await supabase.rpc('get_my_org_basic');
      return data?.[0] ?? null;
    },
    enabled: !!profile?.organization_id,
  });

  const { data: businessAreas } = useQuery({
    queryKey: ['business_areas'],
    queryFn: async () => {
      const { data, error } = await supabase.from('business_areas').select('id, name').order('name');
      if (error) throw error;
      return data as { id: string; name: string }[];
    },
  });

  const hasOrg = !!profile?.organization_id;

  const [step, setStep] = useState<Step>(hasSeenOnboarding() ? 'personal' : 'onboarding');
  const [birthDateOpen, setBirthDateOpen] = useState(false);
  const [personalInfo, setPersonalInfo] = useState<PersonalInfo>({
    birthDate: undefined,
    cargo: '',
    nombreEmpresa: '',
    sector: '',
    area: ''
  });
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<DiagnosticAnswer[]>(createEmptyAnswers());
  const [result, setResult] = useState<{ level: DiagnosticLevel; levelNumber: number; title: string; description: string; answeredQuestions: number; totalQuestions: number; completionPercentage: number; justification?: string } | null>(null);
  const [viewingDiagId, setViewingDiagId] = useState<string | null>(null);

  const handleOnboardingComplete = () => {
    markOnboardingSeen();
    setStep('personal');
  };

  const [draftPrompt, setDraftPrompt] = useState<{ updatedAt: string; data: DiagnosticDraft } | null>(null);
  const [draftDecided, setDraftDecided] = useState(false);

  const draftKey = user ? `diagnostic_draft_${user.id}` : null;

  // Draft server-side (persistencia dual — localStorage + BD)
  const { data: serverDraft, isLoading: loadingServerDraft } = useLatestParticipantDraft();
  const discardServerDraft = useDiscardParticipantDraft();

  // On mount: check for saved draft (only if no completed diag yet).
  // Se ejecuta una sola vez por user (restoredKeyRef) para no reactivar el prompt
  // cuando el autosave server invalida la query — mismo bug que en enterprise.
  const restoredKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (restoredKeyRef.current === draftKey) return;
    if (!draftKey || isLoading || loadingServerDraft) return;
    if (existingDiag) {
      restoredKeyRef.current = draftKey;
      setDraftDecided(true);
      return;
    }
    restoredKeyRef.current = draftKey;

    const localDraft = loadDraft<DiagnosticDraft>(draftKey);

    const countAnswers = (a: DiagnosticAnswer[] | null | undefined) =>
      Array.isArray(a) ? a.filter(v => v && (v.text?.trim() || v.cannotAnswer)).length : 0;

    const localAnswerCount = countAnswers(localDraft?.data?.answers);
    const serverAnswerCount = countAnswers(serverDraft?.answers ?? null);

    const hasLocalContent = !!localDraft?.data && (
      localAnswerCount > 0 ||
      Object.values(localDraft.data.personalInfo || {}).some(v => v && String(v).trim().length > 0)
    );

    const serverMeta = serverDraft?.dimension_scores?.__draft;
    const hasServerContent = !!serverDraft && (
      serverAnswerCount > 0 ||
      !!(serverMeta?.personalInfo && Object.values(serverMeta.personalInfo).some(v => v && String(v).trim().length > 0))
    );

    if (!hasLocalContent && !hasServerContent) {
      setDraftDecided(true);
      return;
    }

    // Elegir el draft con más respuestas. Empate → el más reciente.
    const localTs = hasLocalContent ? new Date(localDraft!.updatedAt).getTime() : 0;
    const serverTs = hasServerContent ? new Date(serverDraft!.updated_at).getTime() : 0;

    let winner: 'local' | 'server' = 'local';
    if (hasLocalContent && !hasServerContent) winner = 'local';
    else if (!hasLocalContent && hasServerContent) winner = 'server';
    else if (localAnswerCount > serverAnswerCount) winner = 'local';
    else if (serverAnswerCount > localAnswerCount) winner = 'server';
    else winner = serverTs >= localTs ? 'server' : 'local';

    if (winner === 'server') {
      const mergedAnswers = (() => {
        const server = Array.isArray(serverDraft!.answers) ? serverDraft!.answers : [];
        const local = Array.isArray(localDraft?.data?.answers) ? localDraft!.data.answers : [];
        const result = createEmptyAnswers();
        for (let i = 0; i < result.length; i++) {
          const s = server[i];
          const l = local[i];
          if (s && (s.text?.trim() || s.cannotAnswer)) result[i] = s;
          else if (l && (l.text?.trim() || l.cannotAnswer)) result[i] = l;
        }
        return result;
      })();
      const combined: DiagnosticDraft = {
        step: serverMeta?.step ?? (serverAnswerCount > 0 ? 'questions' : 'personal'),
        personalInfo: {
          birthDate: serverMeta?.personalInfo?.birthDate ?? localDraft?.data?.personalInfo?.birthDate ?? undefined,
          cargo: serverMeta?.personalInfo?.cargo ?? localDraft?.data?.personalInfo?.cargo ?? '',
          nombreEmpresa: serverMeta?.personalInfo?.nombreEmpresa ?? localDraft?.data?.personalInfo?.nombreEmpresa ?? '',
          sector: serverMeta?.personalInfo?.sector ?? localDraft?.data?.personalInfo?.sector ?? '',
          area: serverMeta?.personalInfo?.area ?? localDraft?.data?.personalInfo?.area ?? '',
        },
        answers: mergedAnswers,
        currentQuestionIndex: serverMeta?.currentQuestionIndex ?? localDraft?.data?.currentQuestionIndex ?? 0,
      };
      setDraftPrompt({ updatedAt: serverDraft!.updated_at, data: combined });
    } else {
      setDraftPrompt(localDraft!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, isLoading, loadingServerDraft, existingDiag]);

  const handleResumeDraft = () => {
    if (!draftPrompt) return;
    const d = draftPrompt.data;
    setPersonalInfo({
      birthDate: d.personalInfo.birthDate ? new Date(d.personalInfo.birthDate) : undefined,
      cargo: d.personalInfo.cargo || '',
      nombreEmpresa: d.personalInfo.nombreEmpresa || '',
      sector: d.personalInfo.sector || '',
      area: d.personalInfo.area || '',
    });
    setAnswers(d.answers && d.answers.length === diagnosticQuestions.length ? d.answers : createEmptyAnswers());
    setCurrentQuestionIndex(d.currentQuestionIndex || 0);
    setStep(d.step);
    setDraftPrompt(null);
    setDraftDecided(true);
    toast.success('Diagnóstico restaurado');
  };

  const handleDiscardDraft = () => {
    if (draftKey) clearDraft(draftKey);
    discardServerDraft.mutate();
    setDraftPrompt(null);
    setDraftDecided(true);
  };

  // Autosave draft (only during personal/questions steps, and only after user decided about prior draft)
  const draftEnabled =
    !!draftKey && draftDecided !== false && (step === 'personal' || step === 'questions') && !draftPrompt;
  const draftValue: DiagnosticDraft = {
    step: step === 'questions' ? 'questions' : 'personal',
    personalInfo: {
      birthDate: personalInfo.birthDate ? personalInfo.birthDate.toISOString() : undefined,
      cargo: personalInfo.cargo,
      nombreEmpresa: personalInfo.nombreEmpresa,
      sector: personalInfo.sector,
      area: personalInfo.area,
    },
    answers,
    currentQuestionIndex,
  };
  // Autosave dual: localStorage (500ms) + server (1.5s).
  // NO gatear por !existingDiag — cuando el user hace retake tiene existingDiag
  // truthy pero está haciendo un diagnóstico nuevo. El step === personal|questions
  // ya excluye el caso "user viendo resultados" donde no queremos autosave.
  useDraftAutosave(draftKey, draftValue, draftEnabled);
  useParticipantDraftAutosave(
    draftEnabled,
    draftEnabled ? {
      step: step === 'questions' ? 'questions' : 'personal',
      personalInfo: {
        birthDate: personalInfo.birthDate ? personalInfo.birthDate.toISOString() : null,
        cargo: personalInfo.cargo || null,
        nombreEmpresa: personalInfo.nombreEmpresa || null,
        sector: personalInfo.sector || null,
        area: personalInfo.area || null,
      },
      answers,
      currentQuestionIndex,
    } : null,
  );

  // Auto-decide draft if existingDiag loads (no prompt needed since results will display)
  useEffect(() => {
    if (existingDiag && !draftDecided) {
      setDraftDecided(true);
    }
  }, [existingDiag, draftDecided]);


  useEffect(() => {
    if (profile) {
      setPersonalInfo(prev => ({
        ...prev,
        birthDate: prev.birthDate || (profile.birth_date ? new Date(profile.birth_date) : undefined),
        cargo: prev.cargo || profile.position || '',
        nombreEmpresa: prev.nombreEmpresa || (hasOrg ? (orgData?.name || orgName || '') : (profile.company || '')),
        sector: prev.sector || (hasOrg ? (orgData?.sector || '') : (profile.sector || '')),
        area: prev.area || profile.area || '',
      }));
    }
  }, [profile, orgName, orgData, hasOrg]);

  useEffect(() => {
    if (existingDiag) {
      const lvl = existingDiag.maturity_level ?? 0;
      const levelKeys: DiagnosticLevel[] = ['iniciador', 'explorador', 'implementador', 'integrador', 'estratega', 'transformador'];
      const level = levelKeys[lvl] || 'iniciador';
      const info = levelInfo[level];

      const savedAnswers = existingDiag.answers as unknown as DiagnosticAnswer[] | null;
      if (savedAnswers && Array.isArray(savedAnswers)) {
        setAnswers(savedAnswers);
      }

      // Diagnósticos migrados no tienen `answers` pero sí `maturity_level` → considerarlos completos
      const hasAnswers = savedAnswers && Array.isArray(savedAnswers) && savedAnswers.length > 0;
      const answeredCount = hasAnswers
        ? savedAnswers!.filter((a: DiagnosticAnswer) => a.text?.trim().length > 0 && !a.cannotAnswer).length
        : diagnosticQuestions.length;

      const dimScores = existingDiag.dimension_scores as any;

      setResult({
        level,
        levelNumber: info.number,
        title: info.title,
        description: info.description,
        answeredQuestions: answeredCount,
        totalQuestions: diagnosticQuestions.length,
        completionPercentage: Math.round((answeredCount / diagnosticQuestions.length) * 100),
        justification: dimScores?.justification || undefined,
      });
      setStep('results');
    }
  }, [existingDiag]);

  const isPersonalInfoComplete =
    personalInfo.birthDate &&
    personalInfo.cargo &&
    personalInfo.nombreEmpresa &&
    personalInfo.sector &&
    personalInfo.area;

  const handlePersonalInfoChange = (field: keyof PersonalInfo, value: string) => {
    setPersonalInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleAnswerChange = (text: string) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = { ...newAnswers[currentQuestionIndex], text, cannotAnswer: false };
    setAnswers(newAnswers);
  };

  const handleCannotAnswerChange = (checked: boolean) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = { text: checked ? '' : newAnswers[currentQuestionIndex].text, cannotAnswer: checked };
    setAnswers(newAnswers);
  };

  const currentAnswer = answers[currentQuestionIndex];
  const isCurrentQuestionAnswered = currentAnswer.text.trim().length > 0 || currentAnswer.cannotAnswer;

  const syncProfileFields = async () => {
    if (!user) return;
    const updates: Record<string, any> = {};
    if (personalInfo.birthDate) updates.birth_date = format(personalInfo.birthDate, 'yyyy-MM-dd');
    if (personalInfo.cargo) updates.position = personalInfo.cargo;
    if (personalInfo.area) updates.area = personalInfo.area;
    if (!hasOrg) {
      if (personalInfo.nombreEmpresa) updates.company = personalInfo.nombreEmpresa;
      if (personalInfo.sector) updates.sector = personalInfo.sector;
    }
    if (Object.keys(updates).length > 0) {
      await supabase.from('profiles').update(updates).eq('id', user.id);
    }
  };

  const handleNextQuestion = async () => {
    if (currentQuestionIndex < diagnosticQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      // Last question — evaluate with AI
      setStep('evaluating');
      syncProfileFields();

      try {
        const aiResult = await evaluateMutation.mutateAsync({
          answers,
          personalInfo: {
            cargo: personalInfo.cargo,
            nombreEmpresa: personalInfo.nombreEmpresa,
            sector: personalInfo.sector,
            area: personalInfo.area,
          },
        });

        const levelKeys: DiagnosticLevel[] = ['iniciador', 'explorador', 'implementador', 'integrador', 'estratega', 'transformador'];
        const levelNum = aiResult.levelNumber ?? 0;
        const level = (aiResult.level as DiagnosticLevel) || levelKeys[levelNum] || 'iniciador';
        const info = levelInfo[level];
        const answeredCount = answers.filter(a => a.text.trim().length > 0 && !a.cannotAnswer).length;

        setResult({
          level,
          levelNumber: info.number,
          title: info.title,
          description: info.description,
          answeredQuestions: answeredCount,
          totalQuestions: diagnosticQuestions.length,
          completionPercentage: Math.round((answeredCount / diagnosticQuestions.length) * 100),
          justification: aiResult.justification,
        });
        setStep('results');

        saveMutation.mutate(
          {
            answers,
            maturity_level: info.number,
            dimension_scores: {
              ...aiResult.dimensionScores,
              justification: aiResult.justification,
            } as any,
          },
          {
            onSuccess: () => {
              if (draftKey) clearDraft(draftKey);
              toast.success('Diagnóstico completado y guardado');
            },
            onError: () => toast.error('Error al guardar el resultado'),
          }
        );
      } catch (err) {
        console.error('AI evaluation error:', err);
        toast.error('Error al evaluar con IA. Intenta de nuevo.');
        setStep('questions');
      }
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    } else {
      setStep('personal');
    }
  };

  const handleRetakeDiagnostic = () => {
    // Antes de empezar limpio, verificar si hay un draft en progreso (local o server).
    // Si lo hay, mostrar el prompt para que el user decida continuar o descartar.
    const localDraft = draftKey ? loadDraft<DiagnosticDraft>(draftKey) : null;
    const countAnswers = (a: DiagnosticAnswer[] | null | undefined) =>
      Array.isArray(a) ? a.filter(v => v && (v.text?.trim() || v.cannotAnswer)).length : 0;

    const localAnswerCount = countAnswers(localDraft?.data?.answers);
    const serverAnswerCount = countAnswers(serverDraft?.answers ?? null);

    const hasLocalContent = !!localDraft?.data && (
      localAnswerCount > 0 ||
      Object.values(localDraft.data.personalInfo || {}).some(v => v && String(v).trim().length > 0)
    );
    const serverMeta = serverDraft?.dimension_scores?.__draft;
    const hasServerContent = !!serverDraft && (
      serverAnswerCount > 0 ||
      !!(serverMeta?.personalInfo && Object.values(serverMeta.personalInfo).some(v => v && String(v).trim().length > 0))
    );

    if (hasLocalContent || hasServerContent) {
      const localTs = hasLocalContent ? new Date(localDraft!.updatedAt).getTime() : 0;
      const serverTs = hasServerContent ? new Date(serverDraft!.updated_at).getTime() : 0;

      let winner: 'local' | 'server' = 'local';
      if (hasLocalContent && !hasServerContent) winner = 'local';
      else if (!hasLocalContent && hasServerContent) winner = 'server';
      else if (localAnswerCount > serverAnswerCount) winner = 'local';
      else if (serverAnswerCount > localAnswerCount) winner = 'server';
      else winner = serverTs >= localTs ? 'server' : 'local';

      if (winner === 'server') {
        const mergedAnswers = (() => {
          const server = Array.isArray(serverDraft!.answers) ? serverDraft!.answers : [];
          const local = Array.isArray(localDraft?.data?.answers) ? localDraft!.data.answers : [];
          const result = createEmptyAnswers();
          for (let i = 0; i < result.length; i++) {
            const s = server[i];
            const l = local[i];
            if (s && (s.text?.trim() || s.cannotAnswer)) result[i] = s;
            else if (l && (l.text?.trim() || l.cannotAnswer)) result[i] = l;
          }
          return result;
        })();
        const combined: DiagnosticDraft = {
          step: serverMeta?.step ?? (serverAnswerCount > 0 ? 'questions' : 'personal'),
          personalInfo: {
            birthDate: serverMeta?.personalInfo?.birthDate ?? localDraft?.data?.personalInfo?.birthDate ?? undefined,
            cargo: serverMeta?.personalInfo?.cargo ?? localDraft?.data?.personalInfo?.cargo ?? '',
            nombreEmpresa: serverMeta?.personalInfo?.nombreEmpresa ?? localDraft?.data?.personalInfo?.nombreEmpresa ?? '',
            sector: serverMeta?.personalInfo?.sector ?? localDraft?.data?.personalInfo?.sector ?? '',
            area: serverMeta?.personalInfo?.area ?? localDraft?.data?.personalInfo?.area ?? '',
          },
          answers: mergedAnswers,
          currentQuestionIndex: serverMeta?.currentQuestionIndex ?? localDraft?.data?.currentQuestionIndex ?? 0,
        };
        setDraftPrompt({ updatedAt: serverDraft!.updated_at, data: combined });
      } else {
        setDraftPrompt(localDraft!);
      }
      // Reset viewMode al step personal para que el prompt sea visible y el
      // user pueda decidir. draftDecided=false para reactivar el flow.
      setResult(null);
      setStep('personal');
      setDraftDecided(false);
      return;
    }

    // Sin draft: reset limpio
    setResult(null);
    setStep('personal');
    setPersonalInfo({
      birthDate: profile?.birth_date ? new Date(profile.birth_date) : undefined,
      cargo: profile?.position || '',
      nombreEmpresa: hasOrg ? (orgData?.name || '') : (profile?.company || ''),
      sector: hasOrg ? (orgData?.sector || '') : (profile?.sector || ''),
      area: profile?.area || '',
    });
    setAnswers(createEmptyAnswers());
    setCurrentQuestionIndex(0);
  };

  const questionsProgress = ((currentQuestionIndex + 1) / diagnosticQuestions.length) * 100;

  const getLevelColor = (level: DiagnosticLevel) => {
    const levelNum = levelInfo[level]?.number ?? 0;
    if (levelNum <= 1) return 'text-accent';
    if (levelNum <= 3) return 'text-warning';
    return 'text-success';
  };

  const getLevelBgColor = (level: DiagnosticLevel) => {
    const levelNum = levelInfo[level]?.number ?? 0;
    if (levelNum <= 1) return 'bg-accent/10 border-accent/30';
    if (levelNum <= 3) return 'bg-warning/10 border-warning/30';
    return 'bg-success/10 border-success/30';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (step === 'onboarding') {
    return <DiagnosticOnboarding onComplete={handleOnboardingComplete} />;
  }

  return (
    <div className="min-h-screen bg-background p-4 sm:p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Diagnóstico de Nivel de Madurez IA</h1>
            <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground" onClick={() => setStep('onboarding')} title="Ver ayuda">
              <HelpCircle className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-4">
          <div className={`flex items-center gap-2 ${step === 'personal' ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'personal' ? 'bg-primary text-primary-foreground' : 'bg-success text-success-foreground'}`}>
              {step !== 'personal' ? <CheckCircle2 className="w-5 h-5" /> : <User className="w-4 h-4" />}
            </div>
            <span className="text-sm font-medium hidden sm:inline">Información</span>
          </div>
          <div className="w-12 h-0.5 bg-border" />
          <div className={`flex items-center gap-2 ${step === 'questions' || step === 'evaluating' ? 'text-primary' : step === 'results' ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'questions' || step === 'evaluating' ? 'bg-primary text-primary-foreground' : step === 'results' ? 'bg-success text-success-foreground' : 'bg-muted'}`}>
              {step === 'results' ? <CheckCircle2 className="w-5 h-5" /> : <ClipboardList className="w-4 h-4" />}
            </div>
            <span className="text-sm font-medium hidden sm:inline">Evaluación</span>
          </div>
          <div className="w-12 h-0.5 bg-border" />
          <div className={`flex items-center gap-2 ${step === 'results' ? 'text-primary' : 'text-muted-foreground'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'results' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
              <Trophy className="w-4 h-4" />
            </div>
            <span className="text-sm font-medium hidden sm:inline">Resultados</span>
          </div>
        </div>

        {/* Resume draft prompt */}
        {draftPrompt && (
          <Card className="glass-card border-primary/40">
            <CardContent className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="flex-1">
                <p className="font-semibold text-foreground">Tienes un diagnóstico en progreso</p>
                <p className="text-sm text-muted-foreground">
                  Última actualización: {formatDistanceToNow(new Date(draftPrompt.updatedAt), { addSuffix: true, locale: es })}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleDiscardDraft}>Empezar de nuevo</Button>
                <Button size="sm" onClick={handleResumeDraft}>Continuar</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Personal Information Step */}
        {step === 'personal' && (
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="text-xl">Información Personal</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label>Fecha de nacimiento *</Label>
                <Popover open={birthDateOpen} onOpenChange={setBirthDateOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal bg-muted/50",
                        !personalInfo.birthDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {personalInfo.birthDate
                        ? format(personalInfo.birthDate, "PPP", { locale: es })
                        : "Selecciona tu fecha de nacimiento"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={personalInfo.birthDate}
                      onSelect={(date) => {
                        setPersonalInfo(prev => ({ ...prev, birthDate: date }));
                        if (date) setBirthDateOpen(false);
                      }}
                      disabled={(date) => date > new Date() || date < new Date("1930-01-01")}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                      captionLayout="dropdown-buttons"
                      fromYear={1930}
                      toYear={new Date().getFullYear()}
                      locale={es}
                      defaultMonth={personalInfo.birthDate ?? new Date(1990, 0, 1)}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cargo">Cargo *</Label>
                <Input id="cargo" placeholder="Ingresa tu cargo actual" value={personalInfo.cargo} onChange={(e) => handlePersonalInfoChange('cargo', e.target.value)} className="bg-muted/50" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="nombreEmpresa">Nombre de la empresa *</Label>
                {hasOrg ? (
                  <div className="flex items-center gap-2 p-3 rounded-md border border-border bg-muted/30">
                    <Building2 className="w-4 h-4 text-primary shrink-0" />
                    <span className="text-sm font-medium text-foreground">{orgData?.name || orgName || 'Cargando...'}</span>
                    <Badge variant="secondary" className="ml-auto text-xs">Organización</Badge>
                  </div>
                ) : (
                  <Input id="nombreEmpresa" placeholder="Ingresa el nombre de tu empresa" value={personalInfo.nombreEmpresa} onChange={(e) => handlePersonalInfoChange('nombreEmpresa', e.target.value)} className="bg-muted/50" />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="sector">Sector *</Label>
                {hasOrg ? (
                  <div className="flex items-center gap-2 p-3 rounded-md border border-border bg-muted/30">
                    <span className="text-sm text-foreground">{orgData?.sector || 'No configurado'}</span>
                    <Badge variant="secondary" className="ml-auto text-xs">De la organización</Badge>
                  </div>
                ) : (
                  <Input id="sector" placeholder="Ej: Tecnología, Finanzas, Salud..." value={personalInfo.sector} onChange={(e) => handlePersonalInfoChange('sector', e.target.value)} className="bg-muted/50" />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="area">Área o Dependencia *</Label>
                <SearchableCombobox
                  value={personalInfo.area}
                  onSelect={(value) => handlePersonalInfoChange('area', value)}
                  placeholder="Selecciona el área"
                  searchPlaceholder="Buscar área..."
                  triggerClassName="bg-muted/50"
                  options={(businessAreas ?? []).map((area) => ({ value: area.name, label: area.name }))}
                />
              </div>

              <Button onClick={() => setStep('questions')} disabled={!isPersonalInfoComplete} className="w-full mt-4 gradient-primary">
                Siguiente
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Questions Step */}
        {step === 'questions' && (
          <Card className="glass-card">
            <CardHeader className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Pregunta {currentQuestionIndex + 1} de {diagnosticQuestions.length}</span>
                <span className="text-sm font-medium text-primary">{Math.round(questionsProgress)}%</span>
              </div>
              <Progress value={questionsProgress} className="h-2" />
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                {diagnosticQuestions[currentQuestionIndex].title}
              </div>
              <CardTitle className="text-lg leading-relaxed">
                {diagnosticQuestions[currentQuestionIndex].question}
              </CardTitle>
              <div className="space-y-4">
                <Textarea
                  placeholder="Escribe tu respuesta aquí..."
                  value={currentAnswer.text}
                  onChange={(e) => handleAnswerChange(e.target.value)}
                  disabled={currentAnswer.cannotAnswer}
                  className={`min-h-[150px] bg-muted/30 border-border resize-none ${currentAnswer.cannotAnswer ? 'opacity-50' : ''}`}
                />
                <div className="flex items-start space-x-3 p-4 rounded-xl border border-border bg-muted/20">
                  <Checkbox id="cannot-answer" checked={currentAnswer.cannotAnswer} onCheckedChange={(checked) => handleCannotAnswerChange(checked === true)} />
                  <div className="space-y-1">
                    <Label htmlFor="cannot-answer" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-muted-foreground" />
                      No puedo responder esta pregunta
                    </Label>
                    <p className="text-xs text-muted-foreground">No entiendo la pregunta o no tengo el conocimiento/experiencia para responderla</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={handlePreviousQuestion} className="flex-1">
                  <ChevronLeft className="w-4 h-4 mr-2" /> Anterior
                </Button>
                <Button onClick={handleNextQuestion} disabled={!isCurrentQuestionAnswered} className="flex-1 gradient-primary">
                  {currentQuestionIndex === diagnosticQuestions.length - 1 ? 'Evaluar con IA' : 'Siguiente'}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Evaluating Step */}
        {step === 'evaluating' && (
          <Card className="glass-card">
            <CardContent className="py-16 text-center space-y-6">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10">
                <Brain className="w-10 h-10 text-primary animate-pulse" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold text-foreground">Evaluando tus respuestas con IA...</h2>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Estamos analizando la calidad y profundidad de tus respuestas para determinar tu nivel de madurez en liderazgo con IA.
                </p>
              </div>
              <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
            </CardContent>
          </Card>
        )}

        {/* Results Step */}
        {step === 'results' && result && (
          <div className="space-y-6">
            <Card className={`glass-card border-2 ${getLevelBgColor(result.level)}`}>
              <CardContent className="pt-8 pb-8 text-center space-y-6">
                <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${result.levelNumber <= 1 ? 'bg-accent/20' : result.levelNumber <= 3 ? 'bg-warning/20' : 'bg-success/20'}`}>
                  <Trophy className={`w-10 h-10 ${getLevelColor(result.level)}`} />
                </div>
                <div className="space-y-2">
                  <h2 className={`text-3xl font-bold ${getLevelColor(result.level)}`}>{result.title}</h2>
                  <p className="text-muted-foreground max-w-md mx-auto">{result.description}</p>
                </div>
                <div className="pt-4 space-y-4">
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-4xl font-bold text-foreground">{result.completionPercentage}%</span>
                    <span className="text-muted-foreground">completado</span>
                  </div>
                  <div className="max-w-xs mx-auto">
                    <Progress value={result.completionPercentage} className="h-3" />
                  </div>
                  <p className="text-sm text-muted-foreground">Preguntas respondidas: {result.answeredQuestions} de {result.totalQuestions}</p>
                </div>
              </CardContent>
            </Card>

            {/* AI Justification */}
            {result.justification && (
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Brain className="w-5 h-5 text-primary" />
                    Evaluación de la IA
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">{result.justification}</p>
                </CardContent>
              </Card>
            )}

            <LevelLadder currentLevel={result.level} />

            {/* Diagnostic History */}
            {diagnosticHistory.length > 1 && (
              <Card className="glass-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <History className="w-5 h-5 text-primary" />
                    Historial de Evaluaciones
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {diagnosticHistory.map((diag, idx) => {
                      const levelKeys: DiagnosticLevel[] = ['iniciador', 'explorador', 'implementador', 'integrador', 'estratega', 'transformador'];
                      const lvl = diag.maturity_level ?? 0;
                      const levelKey = levelKeys[lvl] || 'iniciador';
                      const info = levelInfo[levelKey];
                      const prevDiag = diagnosticHistory[idx + 1];
                      const prevLevel = prevDiag?.maturity_level ?? null;
                      const isLatest = idx === 0;

                      return (
                        <div
                          key={diag.id}
                          className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors hover:bg-muted/40 ${isLatest ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/20'}`}
                          onClick={() => setViewingDiagId(diag.id)}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${isLatest ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                              {info.number}
                            </div>
                            <div>
                              <p className={`text-sm font-medium ${isLatest ? 'text-foreground' : 'text-muted-foreground'}`}>
                                {info.title}
                                {isLatest && <Badge variant="secondary" className="ml-2 text-xs">Actual</Badge>}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {diag.completed_at ? format(new Date(diag.completed_at), "d MMM yyyy, HH:mm", { locale: es }) : '—'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {prevLevel !== null && (
                              <>
                                {lvl > prevLevel ? (
                                  <TrendingUp className="w-4 h-4 text-success" />
                                ) : lvl < prevLevel ? (
                                  <TrendingDown className="w-4 h-4 text-destructive" />
                                ) : (
                                  <Minus className="w-4 h-4 text-muted-foreground" />
                                )}
                              </>
                            )}
                            <Eye className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Diagnostic Detail Modal */}
            {(() => {
              const viewingDiag = diagnosticHistory.find(d => d.id === viewingDiagId);
              if (!viewingDiag) return null;
              const diagAnswers = (viewingDiag.answers as unknown as DiagnosticAnswer[]) || [];
              const dimScores = viewingDiag.dimension_scores as any;
              const lvlKeys: DiagnosticLevel[] = ['iniciador', 'explorador', 'implementador', 'integrador', 'estratega', 'transformador'];
              const lvlKey = lvlKeys[viewingDiag.maturity_level ?? 0] || 'iniciador';
              const lvlInfo = levelInfo[lvlKey];

              return (
                <Dialog open={!!viewingDiagId} onOpenChange={(open) => !open && setViewingDiagId(null)}>
                  <DialogContent className="max-w-2xl max-h-[85vh] p-0">
                    <DialogHeader className="p-6 pb-0">
                      <DialogTitle className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-sm font-bold">
                          {lvlInfo.number}
                        </div>
                        <div>
                          <span>{lvlInfo.title}</span>
                          <p className="text-xs text-muted-foreground font-normal mt-0.5">
                            {viewingDiag.completed_at ? format(new Date(viewingDiag.completed_at), "d MMM yyyy, HH:mm", { locale: es }) : ''}
                          </p>
                        </div>
                      </DialogTitle>
                    </DialogHeader>
                    <ScrollArea className="max-h-[65vh] px-6 pb-6">
                      {dimScores?.justification && (
                        <div className="mb-4 p-4 rounded-lg bg-primary/5 border border-primary/20">
                          <p className="text-xs font-medium text-primary mb-1 flex items-center gap-1">
                            <Brain className="w-3.5 h-3.5" /> Evaluación IA
                          </p>
                          <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">{dimScores.justification}</p>
                        </div>
                      )}
                      <div className="space-y-4">
                        {diagnosticQuestions.map((q, i) => {
                          const answer = diagAnswers[i];
                          return (
                            <div key={q.id} className="border border-border rounded-lg p-4">
                              <div className="flex items-start gap-2 mb-2">
                                <Badge variant="outline" className="shrink-0 text-xs">{i + 1}</Badge>
                                <div>
                                  <p className="text-xs text-primary font-medium">{q.title}</p>
                                  <p className="text-sm font-medium text-foreground mt-0.5">{q.question}</p>
                                </div>
                              </div>
                              {answer?.cannotAnswer ? (
                                <p className="text-sm italic text-muted-foreground ml-8">No pudo responder esta pregunta</p>
                              ) : answer?.text ? (
                                <p className="text-sm text-muted-foreground ml-8 whitespace-pre-line">{answer.text}</p>
                              ) : (
                                <p className="text-sm italic text-muted-foreground ml-8">Sin respuesta</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  </DialogContent>
                </Dialog>
              );
            })()}

            {/* Invitación al diagnóstico empresarial */}
            <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-secondary/5">
              <CardContent className="pt-6 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground">¿Quieres ver esto para toda tu empresa?</h3>
                  <p className="text-sm text-muted-foreground">
                    El Diagnóstico Empresarial mide la madurez en IA de tu organización en 5 dimensiones y
                    entrega recomendaciones priorizadas.
                  </p>
                </div>
                <Button
                  className="shrink-0"
                  onClick={() => navigate('/diagnostico-empresarial')}
                >
                  Conocer más
                </Button>
              </CardContent>
            </Card>

            <Button variant="outline" onClick={handleRetakeDiagnostic}>
              Repetir Diagnóstico
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
